import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Alert,
  CircularProgress,
  Box,
  FormControl,
  Select,
  MenuItem,
  Stack,
  Card,
  CardContent,
  Chip,
  Tooltip,
  TablePagination,
  InputLabel
} from '@mui/material';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { School as SchoolIcon, DirectionsCar as TransportIcon } from '@mui/icons-material';

const formatDate = (date) => {
  if (!date) return 'No date';
  return format(new Date(`${date.split('T')[0]}T12:00:00Z`), 'MMM d, yyyy');
};

const AssignmentDashboard = () => {
  const [firstTimers, setFirstTimers] = useState([]);
  const [unitMembers, setUnitMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedServiceUnit, setSelectedServiceUnit] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    // Get user data from localStorage
    const user = JSON.parse(localStorage.getItem('user'));
    if (user && user.service_unit) {
      setSelectedServiceUnit(user.service_unit);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [firstTimersRes, membersRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/api/assignments/first-timers?page=${page + 1}&limit=${rowsPerPage}`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          }),
          fetch(`${process.env.REACT_APP_API_URL}/api/assignments/unit-members`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          })
        ]);

        if (firstTimersRes.status === 401 || membersRes.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          navigate('/login', { state: { message: 'Your session has expired. Please log in again.' } });
          return;
        }

        const [firstTimersData, membersData] = await Promise.all([
          firstTimersRes.json(),
          membersRes.json()
        ]);

        if (!firstTimersRes.ok) {
          throw new Error(firstTimersData.error || 'Failed to fetch first timers');
        }
        if (!membersRes.ok) {
          throw new Error(membersData.error || 'Failed to fetch unit members');
        }

        setFirstTimers(firstTimersData.data);
        setTotalCount(firstTimersData.total);
        setUnitMembers(membersData);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAssign = async (firstTimerId, assignedTo) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/assignments/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          first_timer_id: firstTimerId,
          assigned_to: assignedTo
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Assignment Response:', result);
        // Update the firstTimers state with the new assignment data
        setFirstTimers(prev => 
          prev.map(timer => 
            timer.id === firstTimerId 
              ? { 
                  ...timer,
                  is_unassigned: false,
                  assigned_to: {
                    first_name: result.data.assigned_to.first_name,
                    last_name: result.data.assigned_to.last_name,
                    id: result.data.assigned_to.id
                  }
                }
              : timer
          )
        );
        setSuccess('First timer assigned successfully');
      } else {
        setError('Failed to assign first timer');
      }
    } catch (error) {
      console.error('Assignment error:', error);
      setError('Failed to assign first timer');
    }
  };

  return (
    <Container maxWidth="lg">
      <Paper sx={{ p: 2, my: 3 }}>
        <Typography variant="h4" gutterBottom>
          First Timer Assignments
        </Typography>

        {/* Debug Table */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Available Unit Members in {selectedServiceUnit ? `${selectedServiceUnit.charAt(0).toUpperCase() + selectedServiceUnit.slice(1)} Unit` : 'All Units'}
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>S/N</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Role</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {unitMembers.map((member, index) => (
                  <TableRow key={member.id}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{member.first_name} {member.last_name}</TableCell>
                    <TableCell>{member.email}</TableCell>
                    <TableCell>{member.phone_number}</TableCell>
                    <TableCell>
                      {member.role === 'unit_manager' ? 'Unit Manager' :
                       member.role === 'system_admin' ? 'System Admin' :
                       'Unit Member'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </Box>

            {isMobile ? (
              <Stack spacing={2}>
                {firstTimers.map((timer) => (
                  <Card key={timer.id} sx={timer.is_unassigned ? { backgroundColor: 'rgba(255, 152, 0, 0.05)' } : {}}>
                    <CardContent>
                      <Stack spacing={1}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="subtitle1" fontWeight="bold">
                            {timer.first_name} {timer.last_name}
                          </Typography>
                          {timer.is_unassigned ? (
                            <Chip label="Unassigned" color="warning" size="small" />
                          ) : (
                            <Chip label="Assigned" color="success" size="small" />
                          )}
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          Service Date: {formatDate(timer.service_date)}
                        </Typography>
                        <Box>
                          <Typography variant="body2">{timer.phone_number}</Typography>
                          <Typography variant="body2">{timer.email}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {timer.gender || 'Not specified'}
                          </Typography>
                          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                            {timer.is_student && (
                              <Tooltip title="Student">
                                <SchoolIcon fontSize="small" color="info" />
                              </Tooltip>
                            )}
                            {timer.needs_transportation && (
                              <Tooltip title="Needs Transportation">
                                <TransportIcon fontSize="small" color="warning" />
                              </Tooltip>
                            )}
                          </Stack>
                        </Box>
                        {!timer.is_unassigned && (
                          <Typography variant="body2" color="text.secondary">
                            Assigned to: {timer.assigned_to?.first_name} {timer.assigned_to?.last_name}
                          </Typography>
                        )}
                        <FormControl fullWidth size="small">
                          <Select
                            value=""
                            onChange={(e) => handleAssign(timer.id, e.target.value)}
                          >
                            {unitMembers.map((member) => (
                              <MenuItem key={member.id} value={member.id}>
                                {member.first_name} {member.last_name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => navigate(`/first-timer/${timer.id}`)}
                        >
                          View Details
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Status</TableCell>
                      <TableCell>Service Date</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Contact</TableCell>
                      <TableCell>Additional Info</TableCell>
                      <TableCell>Currently Assigned To</TableCell>
                      <TableCell>Assign To</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {firstTimers.map((timer) => (
                      <TableRow 
                        key={timer.id}
                        sx={timer.is_unassigned ? { backgroundColor: 'rgba(255, 152, 0, 0.05)' } : {}}
                      >
                        <TableCell>
                          {timer.is_unassigned ? (
                            <Chip label="Unassigned" color="warning" size="small" />
                          ) : (
                            <Chip label="Assigned" color="success" size="small" />
                          )}
                        </TableCell>
                        <TableCell>
                          {formatDate(timer.service_date)}
                        </TableCell>
                        <TableCell>
                          {timer.first_name} {timer.last_name}
                        </TableCell>
                        <TableCell>
                          <Box>
                            {timer.phone_number}
                            <br />
                            {timer.email}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {timer.gender || 'Not specified'}
                            </Typography>
                            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                              {timer.is_student && (
                                <Tooltip title="Student">
                                  <SchoolIcon fontSize="small" color="info" />
                                </Tooltip>
                              )}
                              {timer.needs_transportation && (
                                <Tooltip title="Needs Transportation">
                                  <TransportIcon fontSize="small" color="warning" />
                                </Tooltip>
                              )}
                            </Stack>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {!timer.is_unassigned && (
                            <Typography variant="body2">
                              {timer.assigned_to?.first_name} {timer.assigned_to?.last_name}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <FormControl fullWidth size="small">
                            <Select
                              value=""
                              onChange={(e) => handleAssign(timer.id, e.target.value)}
                            >
                              {unitMembers.map((member) => (
                                <MenuItem key={member.id} value={member.id}>
                                  {member.first_name} {member.last_name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => navigate(`/first-timer/${timer.id}`)}
                          >
                            View Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </Box>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default AssignmentDashboard; 